import { createSlice } from '@reduxjs/toolkit';
import {
  CHANGE_REQUEST_TAB,
  CHANGE_REQUEST_TABS,
  CHANGE_REQUEST_REVIEW_SCOPE,
  CHANGE_REQUEST_REVIEW_DECISION,
  CHANGE_REQUEST_ROW_STATUS,
} from './constants';
import { normalizeChangeRequestRowStatus } from './changeRequestRowStatus';
import {
  portfolioManagerInitialState,
  getEmptyTabBuckets,
} from './initialState';
import fetchChangeRequestsPanel from './service';

export { fetchChangeRequestsPanel };

function patchTabSectionsAfterReview(
  state,
  tabKey,
  { mode, idSet, nextStatus }
) {
  const bucket = state.categoriesByTab[tabKey];
  if (!bucket?.sections || typeof bucket.sections !== 'object') {
    return;
  }

  Object.keys(bucket.sections).forEach((sectionKey) => {
    const sec = bucket.sections[sectionKey];
    if (!sec?.changeRequests?.length) {
      return;
    }

    sec.changeRequests = sec.changeRequests.map((row) => {
      const idStr = row.id != null ? String(row.id) : '';
      const rowPending =
        normalizeChangeRequestRowStatus(row.status) ===
        CHANGE_REQUEST_ROW_STATUS.PENDING;

      const matches = mode === 'all' ? rowPending : idSet.has(idStr);

      if (!matches) {
        return row;
      }

      return {
        ...row,
        status: nextStatus,
      };
    });

    if (typeof sec.pendingCount === 'number') {
      sec.pendingCount = sec.changeRequests.filter(
        (r) =>
          normalizeChangeRequestRowStatus(r.status) ===
          CHANGE_REQUEST_ROW_STATUS.PENDING
      ).length;
    }
  });
}

export const PortfolioManagerSlice = createSlice({
  name: 'portfolioManager',
  initialState: portfolioManagerInitialState,
  reducers: {
    setActiveChangeRequestTab(state, { payload }) {
      state.activeTabId = payload;
    },
    setChangeRequestsReviewNavigateLoading(state, { payload }) {
      state.changeRequestsReviewNavigateLoading = Boolean(payload);
    },
    resetPortfolioManager() {
      return {
        ...portfolioManagerInitialState,
        categoriesByTab: getEmptyTabBuckets(),
      };
    },
    applyChangeRequestReviewOutcome(state, { payload }) {
      const {
        scope,
        decision,
        activeTabId,
        changeRequestIds,
        summaryDecrement,
        removeSectionCategoryId,
      } = payload;

      const nextStatus =
        decision === CHANGE_REQUEST_REVIEW_DECISION.APPROVE
          ? CHANGE_REQUEST_ROW_STATUS.APPROVED
          : CHANGE_REQUEST_ROW_STATUS.REJECTED;

      const dec = Math.max(0, Number(summaryDecrement) || 0);
      if (!state.summary) {
        state.summary = { pendingCount: 0 };
      }
      state.summary.pendingCount = Math.max(
        0,
        (state.summary.pendingCount ?? 0) - dec
      );

      if (scope === CHANGE_REQUEST_REVIEW_SCOPE.ALL) {
        state.tabs.forEach((tab, index) => {
          state.tabs[index].changeRequestCount = 0;
        });
        CHANGE_REQUEST_TABS.forEach((tabKey) => {
          patchTabSectionsAfterReview(state, tabKey, {
            mode: 'all',
            nextStatus,
          });
        });
        return;
      }

      const idSet = new Set(changeRequestIds ?? []);
      state.tabs.forEach((tab, index) => {
        if (tab.id === activeTabId) {
          state.tabs[index].changeRequestCount = Math.max(
            0,
            (tab.changeRequestCount ?? 0) - idSet.size
          );
        }
      });

      const sectionKey =
        removeSectionCategoryId != null ? String(removeSectionCategoryId) : '';
      if (sectionKey !== '') {
        const bucket = state.categoriesByTab[activeTabId];
        if (bucket?.sections && typeof bucket.sections === 'object') {
          delete bucket.sections[sectionKey];
        }
        return;
      }

      patchTabSectionsAfterReview(state, activeTabId, {
        mode: 'ids',
        idSet,
        nextStatus,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChangeRequestsPanel.pending, (state, action) => {
        const isSoftRefetch =
          Boolean(action.meta.arg?.refetchSoft) && state.panelDataReady;
        // Soft refetch: keep current panel visible (no full-page or tab skeleton).
        if (!isSoftRefetch) {
          state.panelDataReady = false;
          state.changeRequestsReviewNavigateLoading = false;
        }
      })
      .addCase(fetchChangeRequestsPanel.fulfilled, (state, { payload }) => {
        const { _refetchSoft, ...rest } = payload;

        state.panelDataReady = true;
        state.panelDataFetchedAt = rest.panelDataFetchedAt ?? null;
        state.panelPendingSnapshot = rest.panelPendingSnapshot ?? null;
        state.entityType = rest.entityType;
        state.entityId = rest.entityId;
        state.entityName = rest.entityName ?? null;
        state.entityTitle = rest.entityTitle ?? null;
        state.entityStatus = rest.entityStatus ?? null;
        state.canApprove = rest.canApprove;
        state.summary = rest.summary;
        state.tabs = rest.tabs ?? [];
        state.refCode = rest.refCode;

        state.categoriesByTab = {
          ...getEmptyTabBuckets(),
          ...rest.categoriesByTab,
        };

        if (!state.activeTabId) {
          state.activeTabId = CHANGE_REQUEST_TAB.MAIN_INFORMATION;
        }
      })
      .addCase(fetchChangeRequestsPanel.rejected, (state) => {
        state.panelDataReady = true;
        state.changeRequestsReviewNavigateLoading = false;
      });
  },
});

export const {
  setActiveChangeRequestTab,
  setChangeRequestsReviewNavigateLoading,
  resetPortfolioManager,
  applyChangeRequestReviewOutcome,
} = PortfolioManagerSlice.actions;

export default PortfolioManagerSlice.reducer;
